import React, { useContext, useEffect, useState } from "react";
import i18next from "i18next";
import { Box, CircularProgress, TextField, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { Controller, useForm, useWatch } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import _ from "lodash";
import { useHistory } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import { BRAND, FONT, RADIUS, useLightStyles } from "./lightTheme";
import { Context } from "states/Store";
import { getUserByAccountAndId, updateAccountUser, updateUserLanguage } from "services/edge/UserService";
import { ERROR, SUCCESS, REGEXP_EMAIL, REGEXP_NAME, preferredCountries } from "services/common/Constants";
import { isEmpty } from "services/common/Utils";
import { isValidPhoneNumber } from "libphonenumber-js/max";
import { RoleBasedAccessControlContext } from "services/common/RolesUtils";
import { useAuth } from "@ea-webapp/auth";
import UserChangePasswordDialog from "containers/users/user-profile/UserChangePasswordDialog";


const useStyles = makeStyles()(() => ({
  // ── Form-specific styles ──
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "1.25rem",
    "@media (max-width: 560px)": { gridTemplateColumns: "1fr" },
  },
  fieldWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "0.35rem",
  },
  input: {
    "& .MuiOutlinedInput-root": {
      borderRadius: RADIUS.md,
      fontFamily: FONT.family,
      ...FONT.body,
      backgroundColor: BRAND.background,
      "& fieldset": { borderColor: BRAND.border },
      "&:hover fieldset": { borderColor: BRAND.primary },
      "&.Mui-focused fieldset": { borderColor: BRAND.primary },
    },
  },
  inputError: {
    "& .MuiOutlinedInput-root": {
      borderRadius: RADIUS.md,
      fontFamily: FONT.family,
      ...FONT.body,
      "& fieldset": { borderColor: "#d32f2f" },
    },
  },
  helperError: {
    ...FONT.tiny,
    color: "#d32f2f",
    fontFamily: FONT.family,
    marginTop: "3px",
  },
  phoneWrapper: {
    "& .react-tel-input .form-control": {
      width: "100%",
      height: "40px",
      borderRadius: RADIUS.md,
      fontFamily: FONT.family,
      ...FONT.body,
      backgroundColor: BRAND.background,
      border: `1px solid ${BRAND.border}`,
      paddingLeft: "48px",
    },
    "& .react-tel-input .flag-dropdown": {
      borderRadius: `${RADIUS.md} 0 0 ${RADIUS.md}`,
      backgroundColor: BRAND.background,
      border: `1px solid ${BRAND.border}`,
    },
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "0.75rem",
    marginTop: "1.5rem",
  },
  cardBody: {
    padding: "1.5rem",
  },
  passwordRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
    flexWrap: "wrap",
  },
  passwordDesc: {
    ...FONT.body,
    color: BRAND.textMuted,
    fontFamily: FONT.family,
  },
}));

export default function LightProfile() {
  const { classes: s } = useStyles();
  const { classes: light } = useLightStyles();
  const [state, dispatch] = useContext(Context);
  const { isGlobalAdmin } = React.useContext(RoleBasedAccessControlContext);
  const { authState, updateUserInfos } = useAuth();
  const history = useHistory();

  const [user, setUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  const { control, formState: { errors }, handleSubmit, reset } = useForm({ mode: "onChange" });
  const values = useWatch({ control });
  const isEqual = user ? _.isEmpty(values) || _.isEqual(values, _.pick(user, "firstname", "name", "function", "email", "phone")) : true;

  useEffect(() => {
    if (!authState.user || !state.account) return;
    (async () => {
      try {
        const res = await getUserByAccountAndId(state.account.id, authState.user.id);
        const phone = res.content.phone?.startsWith("+")
          ? res.content.phone.substring(1)
          : res.content.phone;
        setUser({ ...res.content, phone: phone?.replace(/\s/g, "") });
      } catch (e) {
        dispatch({ type: "ALERT", alert: { type: ERROR, msg: "INTERNAL_ERROR" } });
      }
    })();
  }, [authState.user, state.account, dispatch]);

  const handleSave = async (data) => {
    if (!isEmpty(errors)) return;
    setSaving(true);
    try {
      await updateAccountUser(state.account.id, authState.user.id, {
        email: data.email,
        name: data.name,
        firstName: data.firstname,
        userFunction: data.function,
        phone: "+" + data.phone,
      }, dispatch);
      updateUserInfos({
        email: data.email,
        preferred_username: data.email,
        family_name: data.name,
        given_name: data.firstname,
        name: `${data.firstname} ${data.name}`,
      });
      dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: "userProfile.updateSuccessInfo" } });
      if (data.email !== user.email) window.location.reload();
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: "USER_UPDATE_ERROR" } });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    reset({ firstname: user.firstname, name: user.name, function: user.function, email: user.email, phone: user.phone });
  };

  const userRoles = user?.roles?.join(", ") || (isGlobalAdmin ? "Super Admin" : "");

  return (
      <>
      <Box className={light.pageRoot}>
        {/* ── Texture header with avatar ── */}
        <Box className={light.pageHeader}>
          <Box className={light.pageHeaderInner}>
            <Box className={light.backBtnWhite} onClick={() => history.goBack()}>
              <ArrowBackIcon style={{ fontSize: "16px" }} />
              {i18next.t("back")}
            </Box>
            {user ? (
              <Typography className={light.pageTitleWhite}>{user.firstname} {user.name}</Typography>
            ) : (
              <Typography className={light.pageTitleWhite}>{i18next.t("profile")}</Typography>
            )}
            {userRoles && (
              <Typography className={light.pageSubtitleWhite}>{userRoles}</Typography>
            )}
          </Box>
        </Box>

        {/* ── Content ── */}
        <Box className={light.pageInner} style={{ marginTop: "-0.75rem", maxWidth: "720px" }}>

          {/* Profile form */}
          <Box className={light.sectionCard}>
            <Box className={light.sectionHead}>
              <PersonOutlineIcon style={{ fontSize: "16px", color: BRAND.textMuted }} />
              <Typography className={light.sectionTitle}>{i18next.t("light.profile.personalInfo")}</Typography>
            </Box>
            <Box className={s.cardBody}>
              {!user ? (
                <Box style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
                  <CircularProgress size={24} style={{ color: BRAND.primary }} />
                </Box>
              ) : (
                <form onSubmit={handleSubmit(handleSave)}>
                  <Box className={s.formGrid}>
                    {/* Prénom */}
                    <Box className={s.fieldWrap}>
                      <Typography className={light.fieldLabel}>{i18next.t("firstName")}</Typography>
                      <Controller
                        control={control}
                        name="firstname"
                        defaultValue={user.firstname}
                        rules={{
                          required: i18next.t("light.profile.required"),
                          pattern: { value: REGEXP_NAME, message: i18next.t("light.profile.invalidFormat") },
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            size="small"
                            className={errors.firstname ? s.inputError : s.input}
                            error={!!errors.firstname}
                            disabled={authState.provisioning}
                            fullWidth
                          />
                        )}
                      />
                      {errors.firstname && <Typography className={s.helperError}>{errors.firstname.message}</Typography>}
                    </Box>

                    {/* Nom */}
                    <Box className={s.fieldWrap}>
                      <Typography className={light.fieldLabel}>{i18next.t("lastName")}</Typography>
                      <Controller
                        control={control}
                        name="name"
                        defaultValue={user.name}
                        rules={{
                          required: i18next.t("light.profile.required"),
                          pattern: { value: REGEXP_NAME, message: i18next.t("light.profile.invalidFormat") },
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            size="small"
                            className={errors.name ? s.inputError : s.input}
                            error={!!errors.name}
                            disabled={authState.provisioning}
                            fullWidth
                          />
                        )}
                      />
                      {errors.name && <Typography className={s.helperError}>{errors.name.message}</Typography>}
                    </Box>

                    {/* Fonction */}
                    <Box className={s.fieldWrap}>
                      <Typography className={light.fieldLabel}>{i18next.t("function")}</Typography>
                      <Controller
                        control={control}
                        name="function"
                        defaultValue={user.function}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            size="small"
                            className={s.input}
                            disabled={authState.provisioning}
                            fullWidth
                          />
                        )}
                      />
                    </Box>

                    {/* Email */}
                    <Box className={s.fieldWrap}>
                      <Typography className={light.fieldLabel}>{i18next.t("email")}</Typography>
                      <Controller
                        control={control}
                        name="email"
                        defaultValue={user.email}
                        rules={{
                          required: i18next.t("light.profile.required"),
                          pattern: { value: REGEXP_EMAIL, message: i18next.t("light.profile.invalidEmail") },
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            size="small"
                            type="email"
                            className={errors.email ? s.inputError : s.input}
                            error={!!errors.email}
                            disabled={authState.provisioning}
                            fullWidth
                          />
                        )}
                      />
                      {errors.email && <Typography className={s.helperError}>{errors.email.message}</Typography>}
                    </Box>

                    {/* Téléphone */}
                    <Box className={s.fieldWrap} style={{ gridColumn: "1 / -1" }}>
                      <Typography className={light.fieldLabel}>{i18next.t("phone")}</Typography>
                      <Controller
                        control={control}
                        name="phone"
                        defaultValue={user.phone}
                        rules={{
                          validate: (v) => !v || isValidPhoneNumber("+" + v) || i18next.t("light.profile.invalidPhone"),
                        }}
                        render={({ field }) => (
                          <Box className={s.phoneWrapper}>
                            <PhoneInput
                              country="fr"
                              value={field.value}
                              onChange={field.onChange}
                              preferredCountries={preferredCountries}
                              disabled={authState.provisioning}
                            />
                          </Box>
                        )}
                      />
                      {errors.phone && <Typography className={s.helperError}>{errors.phone.message}</Typography>}
                    </Box>
                  </Box>

                  <Box className={s.actions}>
                    <button type="button" className={light.btnSecondary} style={{ padding: "0.5rem 1.25rem" }} onClick={handleCancel} disabled={isEqual || saving}>
                      {i18next.t("cancel")}
                    </button>
                    <button type="submit" className={light.btnPrimary} style={{ padding: "0.5rem 1.5rem", display: "flex", alignItems: "center", gap: "0.5rem" }} disabled={isEqual || saving}>
                      {saving && <CircularProgress size={14} style={{ color: BRAND.white }} />}
                      {i18next.t("save")}
                    </button>
                  </Box>
                </form>
              )}
            </Box>
          </Box>

          {/* Language */}
          <Box className={light.sectionCard}>
            <Box className={light.sectionHead}>
              <TranslateOutlinedIcon style={{ fontSize: "16px", color: BRAND.textMuted }} />
              <Typography className={light.sectionTitle}>{i18next.t("light.profile.language")}</Typography>
            </Box>
            <Box className={s.cardBody}>
              <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
                <Typography className={s.passwordDesc}>
                  {i18next.t("light.profile.languageDesc")}
                </Typography>
                <select
                  className={light.fieldSelect}
                  style={{ width: "200px" }}
                  value={state.lang || "fr"}
                  onChange={async (e) => {
                    const lang = e.target.value;
                    try {
                      await updateUserLanguage(lang);
                      localStorage.setItem("i18nextLng", lang);
                      dispatch({ type: "LANG", lang });
                      window.location.reload();
                    } catch (_) {}
                  }}
                >
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                  <option value="de">Deutsch</option>
                  <option value="it">Italiano</option>
                  <option value="pt">Português</option>
                  <option value="nl">Nederlands</option>
                  <option value="es">Español</option>
                </select>
              </Box>
            </Box>
          </Box>

          {/* Password */}
          {!authState.provisioning && (
            <Box className={light.sectionCard}>
              <Box className={light.sectionHead}>
                <LockOutlinedIcon style={{ fontSize: "16px", color: BRAND.textMuted }} />
                <Typography className={light.sectionTitle}>{i18next.t("light.profile.password")}</Typography>
              </Box>
              <Box className={s.cardBody}>
                <Box className={s.passwordRow}>
                  <Typography className={s.passwordDesc}>
                    {i18next.t("light.profile.passwordDesc")}
                  </Typography>
                  <button className={`${light.btnSecondary} ${light.btnSmall}`} onClick={() => setPasswordDialogOpen(true)}>
                    {i18next.t("update")}
                  </button>
                </Box>
              </Box>
            </Box>
          )}

        </Box>
      </Box>

      <UserChangePasswordDialog
        isOpen={passwordDialogOpen}
        handleClose={() => setPasswordDialogOpen(false)}
      />
      </>
  );
}
